/**
 * Helpers puros para movimiento de timers de producto.
 * Fuente única compartida entre vista y store (evita divergencia de lógica duplicada).
 */

/**
 * ID base de estado sin sufijo de canal (_channelN).
 * No usar split('_')[0]: truncaría IDs base con guiones bajos.
 *
 * @param {string|number|null|undefined} stateId
 * @returns {string|number|null|undefined}
 */
export function timerProductStateBaseId(stateId) {
    if (stateId == null || stateId === '') {
        return stateId
    }
    const s = String(stateId)
    return s.includes('_channel') ? s.split('_channel')[0] : s
}

/**
 * @param {{ movedBackTimerCount: number, indexField: number, movedTimerCount1: number, movedTimerCount2: number }} counts
 * @returns {number}
 */
export function computeQuantityMove({ movedBackTimerCount, indexField, movedTimerCount1, movedTimerCount2 }) {
    return movedBackTimerCount > 1 ? movedBackTimerCount : indexField === 0 ? movedTimerCount1 : movedTimerCount2
}

/**
 * @param {Object} timerData
 * @param {number} quantityMove
 * @returns {number}
 */
export function computeRemainingUnits(timerData, quantityMove) {
    let remaining = 0
    if (timerData.quantity_avaibles !== 'NaN') {
        remaining = (timerData.quantity_avaibles ? timerData.quantity_avaibles : timerData.quantity) - quantityMove
    }
    return remaining
}

/**
 * @param {number} remainingUnits
 * @param {Object} timerData
 * @returns {boolean}
 */
export function shouldDeleteSourceAfterMove(remainingUnits, timerData) {
    return remainingUnits === 0 || timerData.quantity_avaibles === 'NaN'
}

/**
 * @param {number|string} quantityMove
 * @param {number|string} parentQuantityAvaibles
 * @returns {number}
 */
export function computeNewParentQuantity(quantityMove, parentQuantityAvaibles) {
    return parseInt(quantityMove, 10) + parseInt(parentQuantityAvaibles, 10)
}

/**
 * @param {Object} timers
 * @param {string|number} parentTimerId
 * @param {string} nextStateIdClean
 * @returns {boolean}
 */
export function isParentAtDestination(timers, parentTimerId, nextStateIdClean) {
    if (!timers[parentTimerId]) {
        return false
    }
    const parentTimer = timers[parentTimerId]
    const parentStateClean = timerProductStateBaseId(parentTimer.state)
    return parentStateClean === nextStateIdClean
}

/**
 * Busca timer padre vía timer_rel_id cuando el padre está en el estado destino.
 *
 * @param {Object} timers
 * @param {Object} timer
 * @param {string} nextStateIdClean
 * @returns {Object|null}
 */
export function findParentTimerByRelId(timers, timer, nextStateIdClean) {
    if (!timer.data.timer_rel_id) {
        return null
    }
    const parentTimerId = timer.data.timer_rel_id
    if (isParentAtDestination(timers, parentTimerId, nextStateIdClean)) {
        return timers[parentTimerId]
    }
    return null
}

/**
 * @param {Array} timersRelations
 * @param {string} nextStateIdClean
 * @returns {Object|null}
 */
export function findRelationForDestination(timersRelations, nextStateIdClean) {
    return timersRelations.find((rel) => {
        const relStateClean = timerProductStateBaseId(rel.current_state)
        return relStateClean === nextStateIdClean
    }) || null
}

/**
 * Busca timer padre vía timers_relations cuando el padre está en el estado destino.
 *
 * @param {Object} timers
 * @param {Object} timer
 * @param {string} nextStateIdClean
 * @returns {{ parentTimer: Object|null, relation: Object|null, timersRelations: Array }}
 */
export function findParentTimerByRelations(timers, timer, nextStateIdClean) {
    const timersRelations = timer.data.timers_relations ? JSON.parse(timer.data.timers_relations) : []
    const relation = findRelationForDestination(timersRelations, nextStateIdClean)
    if (!relation || !relation.id_timer_rel) {
        return { parentTimer: null, relation, timersRelations }
    }
    const parentTimerId = relation.id_timer_rel
    if (isParentAtDestination(timers, parentTimerId, nextStateIdClean)) {
        return { parentTimer: timers[parentTimerId], relation, timersRelations }
    }
    return { parentTimer: null, relation, timersRelations }
}

/**
 * Resuelve padre para el branch hasRelationId (usa nextState.id, no params.next_state).
 *
 * @param {Object} timers
 * @param {Object} timer
 * @param {Object} nextState
 * @returns {Object|null}
 */
export function resolveParentTimerForHasRelation(timers, timer, nextState) {
    const nextStateIdClean = timerProductStateBaseId(nextState.id)

    if (timer.data.timer_rel_id && !timer.data.timers_relations) {
        const parentTimerId = timer.data.timer_rel_id
        if (timers[parentTimerId]) {
            const parentTimer = timers[parentTimerId]
            const parentStateClean = timerProductStateBaseId(parentTimer.state)
            if (parentStateClean === nextStateIdClean) {
                return parentTimer
            }
        }
        return null
    }

    if (timer.data.timers_relations) {
        const { parentTimer } = findParentTimerByRelations(timers, timer, nextStateIdClean)
        return parentTimer
    }

    return null
}

/**
 * Fusiona relaciones de timers y añade la relación de columna actual si no existe.
 *
 * @param {{ sourceRelations: Array, destRelations: Array, timerId: string|number, currentStateId: string|number, nextStateId: string|number }} params
 * @returns {{ timersRelations: Array, columnExists: boolean, timersRelationsJson: string }}
 */
export function mergeTimersRelations({ sourceRelations, destRelations, timerId, currentStateId, nextStateId }) {
    const timersRelations = [...sourceRelations]

    destRelations.forEach((rel2) => {
        const isDuplicate = timersRelations.some((rel1) =>
            rel1.id_timer_rel === rel2.id_timer_rel &&
            rel1.current_state === rel2.current_state &&
            rel1.next_state === rel2.next_state
        )
        if (!isDuplicate) {
            timersRelations.push(rel2)
        }
    })

    const columnExists = timersRelations.some((relation) => relation.current_state === currentStateId)

    if (!columnExists) {
        timersRelations.push({
            id_timer_rel: timerId,
            current_state: currentStateId,
            next_state: nextStateId
        })
    }

    return {
        timersRelations,
        columnExists,
        timersRelationsJson: JSON.stringify(timersRelations)
    }
}
